export type RentalFrequency = "Monthly" | "Quarterly";
export type CaseType = "Pre GST" | "Post GST" | "Post TDS";

export const ASSET_CATEGORIES = [
  "Plant & Machinery",
  "Vehicles",
  "IT Hardware",
  "Medical Equipment",
  "Office Equipment",
  "Construction Equipment",
  "Furniture & Fixtures",
  "Renewable Energy",
  "Material Handling",
  "Lab Equipment",
  "Other",
] as const;

export const INDUSTRY_LIST = [
  "Manufacturing",
  "IT / Software",
  "Healthcare",
  "Pharma",
  "Logistics",
  "Retail",
  "Real Estate",
  "Construction",
  "FMCG",
  "Education",
  "Hospitality",
  "Agriculture",
  "Textiles",
  "Automotive",
  "Chemicals",
  "Mining",
  "Telecom",
  "BFSI",
  "Media",
  "Gambling",
  "Crypto",
  "Tobacco",
] as const;

export const FREQUENCY_OPTIONS: RentalFrequency[] = ["Monthly", "Quarterly"];
export const CASE_TYPE_OPTIONS: CaseType[] = ["Pre GST", "Post GST", "Post TDS"];

export interface AssetCategoryRule {
  category: string;
  tenureMin: number;
  tenureMax: number;
  preferredAssets?: string[];
}

export interface InvestmentPreference {
  minExposure: number;
  maxExposure: number;
  preferredProducts: string[];
  discountingFactorMin: number;
  discountingFactorMax: number;
  frequency: RentalFrequency[];
  caseTypes: CaseType[];
  assetCategoryRules: AssetCategoryRule[];
  negativeIndustry: string[];
  /** @deprecated removed from UI; retained as empty array for back-compat with matching engine. */
  minCreditRating: string;
  minTurnover: number;
  note?: string;
  /** @deprecated removed; kept for back-compat reads. */
  processingFeePercent?: number;
  /** All preferred assets from policy_asset_category_preffered.preferred_assets */
  policyPreferredAssets?: string[];
}

export interface LenderPolicy {
  id: string;
  name: string;
  active: boolean;
  preferences: InvestmentPreference;
  updatedBy: string;
  updatedAt: string;
  createdAt: string;
}

export interface ManagedLender {
  id: string;
  name: string;
  logo?: string;
  type: "NBFC" | "Bank" | "AIF";
  policies: LenderPolicy[];
  /** @deprecated */
  preferences?: InvestmentPreference;
  createdAt: string;
}

export type AllocationStatus = "Allocated" | "Reallocated" | "Rejected" | "Updated Exposure";

export interface SimulationOverride {
  lenderId: string;
  policyId?: string;
  rejected?: boolean;
  acceptedExposure?: number;
}

export interface MatchResult {
  lender: ManagedLender;
  suggestedAllocation: number;
  reasons: string[];
  policyId?: string;
  policyName?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────

export function emptyPreferences(): InvestmentPreference {
  return {
    minExposure: 0,
    maxExposure: 0,
    preferredProducts: [],
    discountingFactorMin: 0,
    discountingFactorMax: 0,
    frequency: [],
    caseTypes: [],
    assetCategoryRules: [],
    negativeIndustry: [],
    minCreditRating: "",
    minTurnover: 0,
    note: "",
  };
}

function pickValue<T>(obj: Record<string, unknown> | null | undefined, ...keys: string[]): T | undefined {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null) return value as T;
  }
}

function normalizePolicyActive(record: Record<string, unknown>): boolean {
  const raw = pickValue<unknown>(record, "active", "is_active", "isActive", "status", "policy_status");
  if (raw === undefined || raw === null) return false;

  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw === 1;

  if (typeof raw === "string") {
    const value = raw.trim().toLowerCase();
    if (["1", "true", "active", "yes"].includes(value)) return true;
    if (["0", "false", "inactive", "no"].includes(value)) return false;
  }

  return Boolean(raw);
}

function normalizeAssetCategoryRules(raw: unknown): AssetCategoryRule[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((rule) => {
    const r = rule as Record<string, unknown>;
    const categoryId =
      pickValue(r, "category", "asset_category_id", "category_id", "assetCategoryId") ??
      pickValue(r, "id");
    return {
      category: String(categoryId ?? ""),
      tenureMin: Number(pickValue(r, "tenureMin", "tenure_min") ?? 0),
      tenureMax: Number(pickValue(r, "tenureMax", "tenure_max") ?? 0),
      preferredAssets: (pickValue<unknown[]>(r, "preferredAssets", "preferred_assets") ?? [])
        .map((a) => String(a ?? "").trim())
        .filter(Boolean),
    };
  }).filter((rule) => rule.category);
}

function normalizeAssetCategoryRulesFromPrefs(prefs: Record<string, unknown>): AssetCategoryRule[] {
  const rules = normalizeAssetCategoryRules(
    pickValue(prefs, "assetCategoryRules", "asset_category_rules")
  );
  if (rules.length > 0) return rules;

  const ids = pickValue<unknown[]>(prefs, "asset_categories", "asset_category_ids");
  if (!Array.isArray(ids)) return [];

  return ids
    .map((id) => ({
      category: String(id),
      tenureMin: 0,
      tenureMax: 0,
      preferredAssets: [] as string[],
    }))
    .filter((rule) => rule.category);
}

/** Map API / stored policy preferences into the form shape. */
export function normalizeInvestmentPreference(raw: unknown): InvestmentPreference {
  if (!raw) return emptyPreferences();

  let src = raw;
  if (typeof raw === "string") {
    try {
      src = JSON.parse(raw);
    } catch {
      return emptyPreferences();
    }
  }

  const record = src as Record<string, unknown>;
  const prefs = (record.preferences && typeof record.preferences === "object"
    ? record.preferences
    : record) as Record<string, unknown>;

  return {
    minExposure: Number(pickValue(prefs, "minExposure", "min_exposure") ?? 0),
    maxExposure: Number(pickValue(prefs, "maxExposure", "max_exposure") ?? 0),
    preferredProducts: (pickValue<string[]>(prefs, "preferredProducts", "preferred_products") ?? []).map(String),
    discountingFactorMin: Number(pickValue(prefs, "discountingFactorMin", "discounting_factor_min") ?? 0),
    discountingFactorMax: Number(pickValue(prefs, "discountingFactorMax", "discounting_factor_max") ?? 0),
    frequency: (pickValue<RentalFrequency[]>(prefs, "frequency") ?? []) as RentalFrequency[],
    caseTypes: (pickValue<CaseType[]>(prefs, "caseTypes", "case_types") ?? []) as CaseType[],
    assetCategoryRules: normalizeAssetCategoryRulesFromPrefs(prefs),
    negativeIndustry: (
      pickValue<unknown[]>(prefs, "negativeIndustry", "negative_industry", "negative_industry_ids") ?? []
    ).map(String),
    minCreditRating: String(pickValue(prefs, "minCreditRating", "min_credit_rating") ?? ""),
    minTurnover: Number(pickValue(prefs, "minTurnover", "min_turnover") ?? 0),
    note: String(pickValue(prefs, "note") ?? ""),
  };
}

/** Map API policy record into `LenderPolicy` for table display and edit prefill. */
export function normalizeLenderPolicy(raw: unknown): LenderPolicy {
  const record = (raw ?? {}) as Record<string, unknown>;
  return {
    id: String(pickValue(record, "id", "policy_id") ?? ""),
    name: String(pickValue(record, "name", "policy_name") ?? ""),
    active: normalizePolicyActive(record),
    preferences: normalizeInvestmentPreference(record),
    updatedBy: String(pickValue(record, "updatedBy", "updated_by") ?? ""),
    updatedAt: String(pickValue(record, "updatedAt", "updated_at") ?? ""),
    createdAt: String(pickValue(record, "createdAt", "created_at") ?? ""),
  };
}

/** Mandatory fields across the 3 tabs (10 checks). */
export function policyCompletion(p: InvestmentPreference): number {
  const checks = [
    (p.preferredProducts || []).length > 0,
    p.minExposure > 0,
    p.maxExposure > 0,
    p.discountingFactorMin > 0,
    p.discountingFactorMax > 0,
    (p.frequency || []).length > 0,
    (p.caseTypes || []).length > 0,
    (p.assetCategoryRules || []).length > 0,
    !!p.minCreditRating,
    p.minTurnover > 0,
  ];
  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
}

export function deriveProductType(p: InvestmentPreference): string {
  const products = p.preferredProducts || [];
  if (products.length === 0) return "—";
  if (products.length === 1) return products[0];
  return `${products[0]} +${products.length - 1}`;
}

export function pickActivePolicy(lender: ManagedLender): LenderPolicy | undefined {
  const actives = (lender.policies || []).filter(p => p.active);
  if (actives.length === 0) return undefined;
  return [...actives].sort((a, b) => (b.preferences.maxExposure || 0) - (a.preferences.maxExposure || 0))[0];
}
